"use client";

import { useTranslations } from "next-intl";
import { StarRating } from "@/components/ui/star-rating";
import { useDateFormatter } from "@/lib/i18n/date";
import type { FeedbackResponseDto } from "@/types/feedback";
import { FeedbackStatusBadge } from "./FeedbackStatusBadge";
import { FeedbackTypeBadge } from "./FeedbackTypeBadge";

interface FeedbackDetailProps {
  feedback: FeedbackResponseDto;
}

export const FeedbackDetail: React.FC<FeedbackDetailProps> = ({ feedback }) => {
  const t = useTranslations("feedback");
  const df = useDateFormatter();
  const createdDate = df.numeric(feedback.created_at);
  const updatedDate = df.numeric(feedback.updated_at);

  return (
    <div className="bg-bg-secondary rounded-xl p-4 md:p-6 space-y-4">
      {/* Rating */}
      <div className="flex items-center gap-2">
        <StarRating value={feedback.rating} readonly size="md" />
        <span className="text-sm text-text-primary font-medium">
          {feedback.rating}
        </span>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 items-center">
        <FeedbackTypeBadge type={feedback.type} />
        <span className="text-xs text-text-secondary">
          {t(`department.${feedback.department}`)}
        </span>
        <FeedbackStatusBadge status={feedback.status} />
      </div>

      {/* Related page */}
      {feedback.related_page && (
        <p className="text-xs text-text-secondary">
          {t("detail.relatedPage")}
          {feedback.related_page}
        </p>
      )}

      <div className="border-t border-stroke-light" />

      {/* Full message */}
      <p className="text-sm text-text-primary leading-relaxed">
        {feedback.message}
      </p>

      <div className="border-t border-stroke-light" />

      {/* Dates */}
      <div className="space-y-1">
        <p className="text-xs text-text-secondary">
          {t("detail.createdDate")}
          {createdDate}
        </p>
        <p className="text-xs text-text-secondary">
          {t("detail.updatedDate")}
          {updatedDate}
        </p>
      </div>
    </div>
  );
};
